import { ChangeDetectorRef, Component, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NavigationEnd, Router, RouterEvent } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GlobalSpinnerService } from 'src/app/shared/services/global-spinner.service';
import { LoginCompanyPickerDialogComponent } from 'src/app/components/login-company-picker-dialog/login-company-picker-dialog.component';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { EnvironmentService } from 'src/app/core/services/environment/environment.service';
import { AuthenticationUserCompany } from 'src/app/models/auth/authentication-response.model';
import { AuthService } from 'src/app/services/auth.service';
import { Constants } from 'src/app/services/constants';
import { MentionEvent, MentionsService } from 'src/app/services/mentions.service';
import { ApplicationMode, NavigationService } from 'src/app/services/navigation.service';
import { DrawerComponent } from 'src/app/shared/components/drawer/drawer.component';
import { AlertsCreatedEvent, AlertsService } from 'src/app/services/alerts.service';
import { combineLatest, forkJoin, Subscription } from 'rxjs';
import { AlertModel, AlertType } from 'src/app/models/alert.model';
import { ApplicationContextService } from 'src/app/services/application-context.service';
import { WizardUrl } from 'src/app/models';
import { copyToClipboard } from 'src/utils';
import { NotificationService } from 'src/app/services/notification.service';
import { SignalRService } from 'src/app/services/signalr.service';
import { HubConnectionState } from '@microsoft/signalr';
import { CacheService } from 'src/app/services/cache.service';
import Swal, { SweetAlertResult } from 'sweetalert2';
import { SelectUserModalComponent } from 'src/app/components/select-user-modal/select-user-modal.component';
import { User } from 'src/app/models/user/user.model';
import { show as intercomShow } from '@intercom/messenger-js-sdk';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { ColorSchemePreference } from 'src/app/core/services/theme/theme.constants';

@Component({
    selector: 'header-actions',
    templateUrl: './header-actions.component.html',
    styleUrls: ['./header-actions.component.scss'],
    standalone: false
})
export class HeaderActionsComponent implements OnDestroy, OnInit {

  @ViewChild("mentions")
  mentionsDrawer: DrawerComponent;

  @Input() user;

  @Input()
  unreadConversations: boolean;

  @Output()
  mentionsClicked: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  niceInContactClicked: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  dialpadClicked: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  notificationsClicked: EventEmitter<Notifications> = new EventEmitter<Notifications>();

  @Output()
  alertsCreated: EventEmitter<AlertModel[]> = new EventEmitter<AlertModel[]>();

  @Output()
  conversationsToggled: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  testAudioSettingsClicked: EventEmitter<boolean> = new EventEmitter<boolean>();

  companies: AuthenticationUserCompany[] = [];

  areNotificationsVisible = false;

  alertsAndNotifications: Notifications = new Notifications();

  unreadMentionCount: number = 0;

  /** True when at least one of the items collapsed into the overflow menu
   *  (alerts/notifications, mentions) has unread state. Drives the pulsing
   *  red dot on the overflow trigger so users can tell at a glance that
   *  something inside the "..." menu needs attention without having to open
   *  it. Conversations is excluded because it stays outside the overflow
   *  menu in compact mode and carries its own visible badge. */
  get hasOverflowIndicator(): boolean {
    return (
      (this.alertsAndNotifications?.totalAlertCount ?? 0) > 0 ||
      this.unreadMentionCount > 0
    );
  }

  searchText: string = '';

  searchMode: string = 'Contains';

  loading = false;
  ssnPattern: RegExp = Constants.regexPatterns.ssn;

  mentionsDrawerIsVisible: boolean = false;
  applicationMode: ApplicationMode;

  companyId: number;
  wizardUrls: WizardUrl[] = [];
  restrictFromEditingProfile: boolean = true;
  currentUserIsPrimaryRoleUser: boolean = false;
  isSuperAdmin: boolean = false;
  isAdmin: boolean = false;
  niceInContactEnabled: boolean = true;
  isDisconnected: boolean = true;
  usersReportingTo: User[] = [];
  hasImpersonateAdminUserRight: boolean = false;
  protected isCompanyPHM: boolean = false;
  protected isIntercomEnabled: boolean = false;
  version: string = '';

  appearanceAvailable: boolean = false;
  // No saved choice renders light, so Light is what an unset preference shows as selected.
  appearance: ColorSchemePreference = 'light';

  intervalId: NodeJS.Timeout;

  private _currentRoute: string;

  private _routerEventSubscription: any;

  private _applicationContextSubscription: Subscription;

  private _alertsSubscription: Subscription;
  private _mentionsSubscription: Subscription;
  private _appearanceSubscription: Subscription;

  constructor(
    private readonly _alertsService: AlertsService,
    private readonly _authService: AuthService,
    private readonly _navigationService: NavigationService,
    private readonly _localStorageService: LocalStorageService,
    private readonly _modalService: NgbModal,
    private readonly _router: Router,
    private readonly _spinner: GlobalSpinnerService,
    private readonly _mentionsService: MentionsService,
    private readonly _notifyService: NotificationService,
    private readonly _signalRService: SignalRService,
    private readonly _cacheService: CacheService,
    private readonly _applicationContentextService: ApplicationContextService,
    private readonly _ngZone: NgZone,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _environmentService: EnvironmentService,
    private readonly _themeService: ThemeService
  ) {
    this.version = this._environmentService.version;
    this.applicationMode = this._navigationService.applicationMode;
    this._applicationContextSubscription = this._applicationContentextService.context.subscribe(context => {
      this.companyId = context.userPermissions.companyId;
      this.wizardUrls = context.userPermissions.onlineApplications?.wizardUrls.filter(x => x.isUserDefault) ?? [];
      this.currentUserIsPrimaryRoleUser = context.globalConfig.firstRole?.roleId == context.userPermissions.roleId;
      this.isSuperAdmin = context.userPermissions.superAdmin;
      this.isCompanyPHM = context.isCompanyPHM;
      this.isAdmin = context.userPermissions.admin;
      // const isAllowedCompany = context.isCompanyLodasoft;
      const isAllowedCompany = false;
      const env = this._environmentService.environment;
      const isDevEnv = env === 'loda-dv' || env === 'Local' || env === 'loda-local';
      this.isIntercomEnabled = isDevEnv || isAllowedCompany;
      this.usersReportingTo = context.globalConfig.usersReportingTo;
      this.restrictFromEditingProfile = context.userPermissions.restrictFromEditingProfile;
      this.hasImpersonateAdminUserRight = context.currentlyLoggedInUserProfile?.userProfile?.hasImpersonateAdminUserRight;
    });
    this._alertsSubscription = this._alertsService.events.subscribe((event: AlertsCreatedEvent) => {
      if (!event) {
        return;
      }
      this.alertsCreated.emit(event.alerts);
    });

    this._mentionsSubscription = this._mentionsService.events.subscribe((event: MentionEvent) => {
      if (!event) return;
      if (event.type === "reload") {
        setTimeout(this.getUnreadMentionsCount, 1000);
      }
    })

    this.alertsAndNotifications = this.initializeAlertsAndNotifications();

    this._appearanceSubscription = combineLatest([this._themeService.available$, this._themeService.preference$])
      .subscribe(([available, preference]) => {
        this.appearanceAvailable = available;
        this.appearance = preference ?? 'light';
      });

    this._routerEventSubscription = this._router.events.subscribe(
      (event) => {
        if (event instanceof NavigationEnd) {
          this._currentRoute = event.url;
        }
      }
    );
  }

  ngOnInit() {
    this.loading = true;
    const authData = this._localStorageService.authorizationData;
    this.companies = authData.companies;

    this.getAlerts([]);

    // Run interval outside Angular zone to avoid triggering change detection every second
    this._ngZone.runOutsideAngular(() => {
      this.intervalId = setInterval(() => {
        const connection = this._signalRService.connection;
        const newState = !connection || connection.state !== HubConnectionState.Connected;
        // Only trigger change detection if the state actually changed
        if (this.isDisconnected !== newState) {
          this.isDisconnected = newState;
          this._cdr.detectChanges();
        }
      }, 1000);
    });
  }

  ngOnDestroy(): void {
    if (this._routerEventSubscription) {
      this._routerEventSubscription.unsubscribe();
    }
    if (this._applicationContextSubscription) {
      this._applicationContextSubscription.unsubscribe();
    }
    if (this._alertsSubscription) {
      this._alertsSubscription.unsubscribe();
    }
    if (this._mentionsSubscription) {
      this._mentionsSubscription.unsubscribe();
    }
    this._appearanceSubscription?.unsubscribe();
    if (this.intervalId)
      clearInterval(this.intervalId);
  }

  copyLink = (wizardUrl: WizardUrl) => {
    copyToClipboard(wizardUrl.url);
    this._notifyService.showInfo("Link copied to clipboard", "Info");
  }

  onClearCacheClicked = () => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'Are you sure you want to clear your cache? This will force reload your page on completion.',
      icon: 'warning',
      showCancelButton: true,
      showCloseButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      reverseButtons: true
    }).then((result: SweetAlertResult) => {
      if (!result.value) {
        return;
      }
      this._cacheService.clearAsync()
        .then(() => {
          this.refreshPage();
        });
    });

  }

  refreshPage(): void {
    window.location.reload();
  }

  onSupportLinkClicked = () => {
    if (this.isIntercomEnabled) {
      intercomShow();
    } else {
      window.open("https://lodasoft.atlassian.net/servicedesk/customer/portals", "_blank");
    }
  }

  showMentions() {
    this.mentionsClicked.emit(true);
  }

  showNiceInContact(activeTab: string) {
    this.niceInContactClicked.emit(activeTab);
  }

  showDialpad() {
    this.dialpadClicked.emit();
  }

  openImpersonateUserPortal = (userCompanyGuid) => {
    this._router.navigate(['impersonate-user'], {
      queryParams: { userCompanyGuid: userCompanyGuid },
    });
  }

  showImpersonateUserDialog = () => {
    const modalOptions = Constants.modalOptions.medium;
    modalOptions.scrollable = false;
    const modalRef = this._modalService.open(
      SelectUserModalComponent,
      modalOptions
    );
    modalRef.componentInstance.users = this.usersReportingTo;
    modalRef.componentInstance.title = 'Impersonate User';

    modalRef.result.then(userId => {
      if (!userId) {
        return;
      }
      this.openImpersonateUserPortal(userId);
    });
  }

  showNotifications() {
    this.areNotificationsVisible = false;
    this.notificationsClicked.emit(this.alertsAndNotifications);
  }

  showConversations = () => {
    this.conversationsToggled.emit(true);
  }

  onAppearanceChosen = (preference: ColorSchemePreference) => {
    this._themeService.choose(preference);
  }

  showTestAudioSettings = () => {
    this.testAudioSettingsClicked.emit(true);
  }

  onSearchIconClicked = () => {
    setTimeout(() => {
      document.getElementById("searchInput")?.focus();
    }, 150);
  }

  /**
   * Invoked from the overflow dropdown when the Search icon is parked offscreen
   * by the compact media query. The search <li> stays live in the DOM (see
   * .header-action-search rules in the component SCSS), so Bootstrap's Dropdown
   * instance is still bound — all we need to do is fire the trigger click.
   */
  onCompactSearchClicked = () => {
    const trigger = document.getElementById("search-bar-animation");
    if (!trigger) return;
    setTimeout(() => {
      trigger.click();
      this.onSearchIconClicked();
    }, 0);
  }

  onLogoutClicked = () => {
    this._spinner.show();
    this._authService.logout().subscribe(
      () => {
        this._spinner.hide();
      },
      (error) => {
        this._spinner.hide();
      }
    );
  };

  onGotoNextGenClicked = () => {
    if (this._currentRoute.indexOf('loda-nextgen') === -1) {
      this._navigationService.applicationMode = ApplicationMode.NextGen;
      const route = this._currentRoute.replace('admin', 'loda-nextgen');
      this._router.navigateByUrl(route);
    }
    else {
      this._navigationService.applicationMode = ApplicationMode.Classic;
      const route = this._currentRoute.replace('loda-nextgen', 'admin');
      this._router.navigateByUrl(route);
    }
  };

  onSwitchCompanyClicked = () => {
    const modalRef = this._modalService.open(
      LoginCompanyPickerDialogComponent,
      Constants.modalOptions.medium
    );
    modalRef.componentInstance.availableCompanies = this.companies;
  };

  search = () => {
    if (this.searchText != '') {
      const url = "admin/search?param=" + encodeURIComponent(this.searchText) + "&searchMode=" + this.searchMode;
      this._router.navigateByUrl(url);

      this.searchText = '';
    }
  }

  private initializeAlertsAndNotifications = (): Notifications => {
    const alertsAndNotifications = new Notifications();

    const borrowerMessagesBucket = new AlertBucket("Borrower Messages", AlertType.BorrowerMessageAlert, 1501);
    const taskStatusChangesBucket = new AlertBucket("Task Status Changes", AlertType.TaskStatusAlert, 1502);
    const notificationsBucket = new AlertBucket("Misc Notifications", AlertType.GeneralAlert, 1503);
    const loanStatusChangesBucket = new AlertBucket("Loan Status Changes", AlertType.LoanStatusAlert, 1504);
    const emailAlertsBucket = new AlertBucket("Failed E-mails", AlertType.FailedEmailAlert, 1505);
    const smsAlertsBucket = new AlertBucket("Failed SMS", AlertType.FailedSmsAlert, 1506);
    const newLeadAlertsBucket = new AlertBucket("New Leads", AlertType.NewLeadAlert, 1507);
    const borrowerPrequalBucket = new AlertBucket("Borrower Generated Prequal", AlertType.BorrowerGeneratedPrequal, 1509);
    const agentPrequalBucket = new AlertBucket("Agent Generated Prequal", AlertType.AgentGeneratedPrequal, 1510);
    const ocrSuccessBucket = new AlertBucket("OCR Completed Success", AlertType.OCRCompleted_Success, 1511);
    const OcrFailureBucket = new AlertBucket("OCR Completed Failure", AlertType.OCRCompleted_Failure, 1512);
    const TridAlertBucket = new AlertBucket("TRID Triggers", AlertType.TridHasBeenTriggered, 1513);
    const voicemailsBucket = new AlertBucket("Voicemails", AlertType.VoicemailAlert, 1515);

    alertsAndNotifications.alertBuckets.push(newLeadAlertsBucket);
    alertsAndNotifications.alertBuckets.push(notificationsBucket);
    alertsAndNotifications.alertBuckets.push(loanStatusChangesBucket);
    alertsAndNotifications.alertBuckets.push(taskStatusChangesBucket);
    alertsAndNotifications.alertBuckets.push(borrowerPrequalBucket);
    alertsAndNotifications.alertBuckets.push(agentPrequalBucket);
    alertsAndNotifications.alertBuckets.push(ocrSuccessBucket);
    alertsAndNotifications.alertBuckets.push(OcrFailureBucket);
    alertsAndNotifications.alertBuckets.push(borrowerMessagesBucket); //TODO: remove me when conversations works fully
    alertsAndNotifications.alertBuckets.push(emailAlertsBucket);
    alertsAndNotifications.alertBuckets.push(smsAlertsBucket);
    alertsAndNotifications.alertBuckets.push(TridAlertBucket);
    alertsAndNotifications.alertBuckets.push(voicemailsBucket);

    return alertsAndNotifications;
  }

  // TODO: adjust to v1
  private getAlerts(data) {
    this.getUnreadMentionsCount();
    this.fillAlertsBuckets(this.alertsAndNotifications);
  }

  // TODO: adjust to v1
  private _updateAlerts(data) {

  }

  private fillAlertsBuckets = (notifications: Notifications) => {
    const apiCalls: any[] = [];
    notifications.alertBuckets.forEach(b => {
      const apiCall = this._alertsService.getAlertsByType(b.typeId);
      apiCalls.push(apiCall);
    });

    if (apiCalls.length) {
      forkJoin(apiCalls).subscribe(results => {
        results.forEach((alerts: AlertModel[], index) => {
          const bucketToFill = notifications.alertBuckets[index];
          if (bucketToFill.type === AlertType.FailedEmailAlert || bucketToFill.type === AlertType.FailedSmsAlert) {
            alerts = alerts.filter((a: AlertModel) => !a.isRead)
          }
          bucketToFill.alerts = alerts;
        })
      }, errors => {

      });
    }
  }

  private getUnreadMentionsCount = () => {
    this._mentionsService.getMentionsCount().subscribe(counts => {
      this.unreadMentionCount = counts.unreadMentionCount ?? 0;
    });
  }
}

export class Notifications {

  alertBuckets: AlertBucket[] = [];

  getAlertsInBucketsOfTypes = (types: AlertType[]) => {
    const buckets = this.alertBuckets.filter(b => types.includes(b.type));
    const allAlerts = buckets.reduce((r, { alerts }) => [...r, ...alerts], []);
    return allAlerts;
  }

  get totalAlertCount(): number {
    const totalAlertCount: number = this.alertBuckets.map(b => b.alerts.length).reduce((a, b) => a + b);
    return totalAlertCount;
  }
}

export class AlertBucket {
  name: string;
  type: AlertType;
  typeId: number;
  alerts: AlertModel[] = [];

  constructor(name: string, type: AlertType, typeId: number) {
    this.name = name;
    this.type = type;
    this.typeId = typeId;
  }
}
